import { useAuth, useUser } from "@clerk/react"
import { useId, useState } from "react"
import type { AccessDecision } from "../auth/config"
import { useStoredState } from "../hooks/useStoredState"
import { useI18n } from "../i18n/useI18n"
import {
  createModerationIncident,
  EMPTY_MODERATION_INCIDENT_STORE,
  MODERATION_INCIDENT_STORAGE_KEY,
  MODERATION_INCIDENT_STORAGE_VERSION,
  type ModerationIncident,
  ModerationIncidentStoreSchema,
} from "../moderation/storage"
import { usePublishPublicProfileMutation } from "../social/query"
import { buildPublicPlayerProfile, createFriendCode } from "./publicProfile"
import {
  EMPTY_GAMER_TAG_REGISTRY,
  EMPTY_PLAYER_PROFILE_STORE,
  GAMER_TAG_REGISTRY_STORAGE_KEY,
  GAMER_TAG_REGISTRY_STORAGE_VERSION,
  GamerTagRegistryStateSchema,
  PLAYER_PROFILES_STORAGE_KEY,
  PLAYER_PROFILES_STORAGE_VERSION,
  PlayerProfileStoreStateSchema,
} from "./storage"
import {
  createProfileId,
  type GamerTagErrorCode,
  getInviteStatusForAccess,
  getProfileByEmail,
  getRoleForAccess,
  normalizeEmail,
  normalizeGamerTag,
  validateGamerTag,
} from "./validation"

type ProfileBootstrapProps = {
  access: Extract<AccessDecision, "invited" | "bootstrap_admin">
  email: string
  alwaysShowForm?: boolean
}

export function ProfileBootstrap({
  access,
  email,
  alwaysShowForm = false,
}: ProfileBootstrapProps) {
  const { t } = useI18n()
  const { userId } = useAuth()
  const { user } = useUser()
  const publishPublicProfileMutation = usePublishPublicProfileMutation()
  const normalizedEmail = normalizeEmail(email)
  const [profiles, setProfiles] = useStoredState(
    PLAYER_PROFILES_STORAGE_KEY,
    PlayerProfileStoreStateSchema,
    {
      defaultValue: EMPTY_PLAYER_PROFILE_STORE,
      version: PLAYER_PROFILES_STORAGE_VERSION,
    },
  )
  const [registry, setRegistry] = useStoredState(
    GAMER_TAG_REGISTRY_STORAGE_KEY,
    GamerTagRegistryStateSchema,
    {
      defaultValue: EMPTY_GAMER_TAG_REGISTRY,
      version: GAMER_TAG_REGISTRY_STORAGE_VERSION,
    },
  )
  const [, setModerationIncidents] = useStoredState(
    MODERATION_INCIDENT_STORAGE_KEY,
    ModerationIncidentStoreSchema,
    {
      defaultValue: EMPTY_MODERATION_INCIDENT_STORE,
      version: MODERATION_INCIDENT_STORAGE_VERSION,
    },
  )
  const existingProfile = getProfileByEmail(profiles, normalizedEmail)
  const [gamerTag, setGamerTag] = useState(existingProfile?.gamerTag ?? "")
  const [submitError, setSubmitError] = useState<string | null>(null)
  const gamerTagInputId = useId()

  const gamerTagError = validateGamerTag(
    gamerTag,
    registry,
    existingProfile?.email ?? normalizedEmail,
  )

  const translateGamerTagError = (error: GamerTagErrorCode | null) => {
    switch (error) {
      case "length":
        return t("profile.gamerTagError.length")
      case "characters":
        return t("profile.gamerTagError.characters")
      case "blocked":
        return t("profile.gamerTagError.blocked")
      case "taken":
        return t("profile.gamerTagError.taken")
      default:
        return error
    }
  }

  const translatedGamerTagError = translateGamerTagError(gamerTagError)

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (gamerTagError) {
      const incidents: ModerationIncident[] = []
      if (gamerTagError === "blocked") {
        incidents.push(
          createModerationIncident("gamer_tag", gamerTag, normalizedEmail),
        )
      }
      if (incidents.length > 0) {
        setModerationIncidents((previous) => [...incidents, ...previous])
      }
      setSubmitError(translatedGamerTagError)
      return
    }

    const now = new Date().toISOString()
    const nextProfile = {
      id: existingProfile?.id ?? createProfileId(normalizedEmail),
      email: normalizedEmail,
      gamerTag: gamerTag.trim(),
      gamerTagNormalized: normalizeGamerTag(gamerTag),
      friendCode: existingProfile?.friendCode ?? createFriendCode(),
      role: getRoleForAccess(access),
      inviteStatus: getInviteStatusForAccess(access),
      createdAt: existingProfile?.createdAt ?? now,
      updatedAt: now,
    } as const

    setProfiles((previous) => ({
      ...previous,
      [normalizedEmail]: nextProfile,
    }))

    setRegistry((previous) => {
      const nextRegistry = { ...previous }

      if (
        existingProfile?.gamerTagNormalized &&
        existingProfile.gamerTagNormalized !== nextProfile.gamerTagNormalized
      ) {
        delete nextRegistry[existingProfile.gamerTagNormalized]
      }

      nextRegistry[nextProfile.gamerTagNormalized] = normalizedEmail
      return nextRegistry
    })

    setSubmitError(null)

    if (!userId) {
      return
    }

    void publishPublicProfileMutation
      .mutateAsync(
        buildPublicPlayerProfile(nextProfile, userId, user?.imageUrl),
      )
      .catch(() => {
        // Keep local profile bootstrap usable when the social sync path
        // is unavailable.
      })
  }

  if (existingProfile && !alwaysShowForm) {
    return (
      <section className="panel panel-success profile-bootstrap-panel">
        <div className="panel-header">
          <div>
            <h2>{t("profile.ready.title")}</h2>
            <p>{t("profile.ready.body")}</p>
          </div>
        </div>

        <dl className="status-list">
          <div>
            <dt>{t("profile.gamerTagLabel")}</dt>
            <dd>{existingProfile.gamerTag}</dd>
          </div>
          <div>
            <dt>{t("common.role")}</dt>
            <dd>
              {existingProfile.role === "admin"
                ? t("profile.role.admin")
                : t("profile.role.player")}
            </dd>
          </div>
        </dl>
      </section>
    )
  }

  return (
    <section className="panel profile-bootstrap-panel">
      <h2>{t("profile.create.title")}</h2>
      <p>{t("profile.create.description")}</p>

      <form className="profile-form" onSubmit={handleSubmit}>
        <label className="field" htmlFor={gamerTagInputId}>
          <span>{t("profile.gamerTagLabel")}</span>
          <input
            id={gamerTagInputId}
            autoCapitalize="none"
            autoCorrect="off"
            className="text-input"
            maxLength={20}
            name="gamerTag"
            onChange={(event) => setGamerTag(event.target.value)}
            placeholder={t("profile.gamerTagPlaceholder")}
            spellCheck={false}
            type="text"
            value={gamerTag}
          />
          <small className="field-help">{t("profile.gamerTagHelp")}</small>
          {gamerTagError ? (
            <strong className="field-error">{translatedGamerTagError}</strong>
          ) : null}
        </label>

        {submitError ? <p className="field-error">{submitError}</p> : null}

        <button className="primary-button" type="submit">
          {t("profile.save")}
        </button>
      </form>
    </section>
  )
}
